import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'motion/react';
import { MouseEvent, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { BuildingIcon, EditIcon, UsersIcon } from '../Icons/CommonIcons';
import { OrgContext } from './OrgContext';
import { Organization } from '@/API';

const OrgList = () => {
  const navigate = useNavigate();
  const { setIsEdit, isEdit, setCurrentOrg, orgs, setShowModal, setEditFocus } =
    useContext(OrgContext);

  useEffect(() => {
    if (isEdit) {
      setShowModal(true);
    }
  }, [isEdit, setShowModal]);

  const handleEdit = (e: MouseEvent, org: Organization, focus: string) => {
    e.stopPropagation();
    setEditFocus(focus);
    setCurrentOrg(org);
    setIsEdit(true);
  };

  const getFreeTrialLabel = (org: Organization) => {
    if (!org.onFreeTrial) {
      return 'Off';
    }

    if (org.freeTrialExpiration) {
      return `On until ${dayjs(org.freeTrialExpiration).format('MM/DD/YYYY')}`;
    }

    return 'On';
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-3xl">
        <div className="grid grid-cols-[2fr_1fr_1fr_1.5fr_1fr_1fr_0.5fr] gap-16 mb-16">
          <div className="px-16 py-8">Organization</div>
          <div className="px-16 py-8">Admins</div>
          <div className="px-16 py-8">Hosts</div>
          <div className="px-16 py-8">Free Trial</div>
          <div className="px-16 py-8">Status</div>
          <div className="px-16 py-8">Users</div>
          <div className="px-16 py-8"></div>
        </div>
        <div className="space-y-8">
          {orgs.map((org) => (
            <div
              key={org.tenant}
              className="grid grid-cols-[2fr_1fr_1fr_1.5fr_1fr_1fr_0.5fr] gap-16 bg-gray-100 items-center hover:bg-gray-200 rounded-3xl cursor-pointer"
              onClick={() => {
                setCurrentOrg(org);
                navigate(`/dashboard/orgs/${org.tenant}/users`);
                window.scrollTo(0, 0);
              }}
            >
              <div className="ml-32 px-16 py-16 flex items-center">
                <BuildingIcon className="mr-8" />
                <span className="font-semibold">{org.name}</span>
              </div>
              <div className="px-16 py-16">
                <motion.span
                  whileHover="hover"
                  initial="initial"
                  className="font-bold hover:text-[#41797b] hover:underline cursor-pointer flex items-center"
                  onClick={(e) => handleEdit(e, org, 'admin')}
                >
                  {org.adminLicenses}
                  <motion.div
                    className="ml-8 mt-4"
                    variants={{
                      hover: { opacity: 1 },
                      initial: { opacity: 0 }
                    }}
                    transition={{ duration: 0.1 }}
                  >
                    <EditIcon />
                  </motion.div>
                </motion.span>
              </div>
              <div className="px-16 py-16">
                <motion.span
                  whileHover="hover"
                  initial="initial"
                  className="font-bold hover:text-[#41797b] hover:underline cursor-pointer flex items-center"
                  onClick={(e) => handleEdit(e, org, 'host')}
                >
                  {org.hostLicenses}
                  <motion.div
                    className="ml-8 mt-4"
                    variants={{
                      hover: { opacity: 1 },
                      initial: { opacity: 0 }
                    }}
                    transition={{ duration: 0.1 }}
                  >
                    <EditIcon />
                  </motion.div>
                </motion.span>
              </div>
              <div className="px-16 py-16">
                <motion.span
                  whileHover="hover"
                  initial="initial"
                  className="font-bold hover:text-[#41797b] hover:underline cursor-pointer flex items-center"
                  onClick={(e) => handleEdit(e, org, 'freeTrial')}
                >
                  {getFreeTrialLabel(org)}
                  <motion.div
                    className="ml-8 mt-4"
                    variants={{
                      hover: { opacity: 1 },
                      initial: { opacity: 0 }
                    }}
                    transition={{ duration: 0.1 }}
                  >
                    <EditIcon />
                  </motion.div>
                </motion.span>
              </div>
              <div className="px-16 py-16">
                <motion.span
                  whileHover="hover"
                  initial="initial"
                  className="font-bold hover:text-[#41797b] hover:underline cursor-pointer flex items-center"
                  onClick={(e) => handleEdit(e, org, 'status')}
                >
                  {org.organizationActive ? 'Active' : 'Paused'}
                  <motion.div
                    className="ml-8 mt-4"
                    variants={{
                      hover: { opacity: 1 },
                      initial: { opacity: 0 }
                    }}
                    transition={{ duration: 0.1 }}
                  >
                    <EditIcon />
                  </motion.div>
                </motion.span>
              </div>
              <div className="px-16 py-16">
                <div className="flex items-center justify-center bg-white border border-gray-600 rounded-full px-8 py-8">
                  <UsersIcon className="mr-8" />
                  <span>{org.activeUsers}</span>
                </div>
              </div>
              <div className="px-16 py-16 flex items-center justify-end">
                <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OrgList;
